import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type ReactNode } from "react";

type RouteState = { pathname: string; search: string };
type NavigateOptions = { replace?: boolean };
type RouterContextValue = RouteState & { navigate: (to: string, options?: NavigateOptions) => void };

const RouterContext = createContext<RouterContextValue | null>(null);

function currentRoute(): RouteState {
  return { pathname: window.location.pathname || "/", search: window.location.search };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<RouteState>(currentRoute);
  useEffect(() => {
    const changed = () => setRoute(currentRoute());
    window.addEventListener("popstate", changed);
    return () => window.removeEventListener("popstate", changed);
  }, []);
  const navigate = useCallback((to: string, options: NavigateOptions = {}) => {
    const url = new URL(to, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error("Navigation must stay on the TAXOS origin");
    const pathChanged = window.location.pathname !== url.pathname;
    if (options.replace) window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
    else window.history.pushState(null, "", `${url.pathname}${url.search}${url.hash}`);
    setRoute(currentRoute());
    if (pathChanged) window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const value = useMemo(() => ({ ...route, navigate }), [route, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouter() {
  const context = useContext(RouterContext);
  if (!context) throw new Error("RouterProvider is missing");
  return context;
}

export function useLocation() {
  const { pathname, search } = useRouter();
  return { pathname, search };
}

export function useNavigate() {
  return useRouter().navigate;
}

export function useSearchParams(): [URLSearchParams, (next: URLSearchParams | Record<string, string>, options?: NavigateOptions) => void] {
  const { pathname, search, navigate } = useRouter();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const setParams = useCallback((next: URLSearchParams | Record<string, string>, options: NavigateOptions = {}) => {
    const value = next instanceof URLSearchParams ? next : new URLSearchParams(next);
    navigate(`${pathname}${value.size ? `?${value.toString()}` : ""}`, options);
  }, [navigate, pathname]);
  return [params, setParams];
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { to: string };
export function Link({ to, onClick, children, ...props }: LinkProps) {
  const navigate = useNavigate();
  return <a href={to} {...props} onClick={(event) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); navigate(to);
  }}>{children}</a>;
}

type NavLinkProps = Omit<LinkProps, "className"> & { end?: boolean; className?: string | ((state: { isActive: boolean }) => string) };
export function NavLink({ to, end = false, className, ...props }: NavLinkProps) {
  const { pathname } = useLocation();
  const targetPath = new URL(to, window.location.origin).pathname;
  const isActive = end ? pathname === targetPath : pathname === targetPath || pathname.startsWith(`${targetPath}/`);
  const resolvedClass = typeof className === "function" ? className({ isActive }) : className;
  return <Link to={to} className={resolvedClass} {...props} />;
}
